import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../../api/ad.api';
import { questionApi } from '../../api/question.api';
import {
  AdminAdDetail,
  AdType,
  AdDurationType,
  AdPosition,
  CreateAdRequest,
  PositionItemRequest,
} from '../../types/Ad.api.type';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { X, ArrowRight, Search, Check, Upload, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { QuestionV2 } from '../../types/Question.type';
import { AD_ICON_PRESETS, getAdIconPreset } from '../../constants/adIcons';
import { isValidSharelinkUrl, parseSharelinkPaste } from '../../util/sharelinkPaste';

/** 기간 프리셋 옵션 */
const DURATION_OPTIONS: { value: AdDurationType; label: string; days: number }[] = [
  { value: 'THREE_DAYS', label: '3일', days: 3 },
  { value: 'ONE_WEEK', label: '1주', days: 7 },
  { value: 'TWO_WEEKS', label: '2주', days: 14 },
  { value: 'ONE_MONTH', label: '1개월', days: 30 },
  { value: 'CUSTOM', label: '직접입력', days: 0 },
];

/** 앱내링크 프리셋 */
const APP_ROUTE_PRESETS: { label: string; route: string; state?: string }[] = [
  { label: '메인 피드', route: '/dashboard' },
  { label: '인기 설문', route: '/dashboard', state: '{"tab":1}' },
  { label: '내 설문', route: '/dashboard', state: '{"tab":2}' },
  { label: '내 정보', route: '/user/dashboard' },
  { label: '출석체크', route: '/user/dashboard', state: '{"tab":1}' },
  { label: '리워드', route: '/user/dashboard', state: '{"tab":2}' },
  { label: '설문 등록', route: '/question/create' },
  { label: '공지사항', route: '/notice' },
  { label: '문의하기', route: '/inquiry' },
];

/** 위치 옵션 */
const POSITION_OPTIONS: { value: AdPosition; label: string; description: string }[] = [
  { value: 'HOME_TOP', label: '홈 상단', description: '홈 인트로 페이지 최상단 배너' },
  { value: 'HOME_MIDDLE', label: '홈 중단', description: '홈 설문목록 카드 하단 배너' },
  { value: 'MAIN_FEED_TOP', label: '메인 피드 상단', description: '메인 피드 최상단 미니배너' },
  { value: 'MAIN_FEED', label: '메인 피드', description: '메인 설문 목록 4번째마다' },
  { value: 'POPULAR_FEED_TOP', label: '인기설문 상단', description: '인기설문 피드 최상단 미니배너' },
  { value: 'POPULAR_FEED', label: '인기설문 피드', description: '인기설문 목록 4번째마다' },
  { value: 'MY_FEED_TOP', label: '내설문 상단', description: '내설문 피드 최상단 미니배너' },
  { value: 'MY_FEED', label: '내설문 피드', description: '내설문 목록 4번째마다' },
  { value: 'DETAIL_COMMENT', label: '상세 댓글 위', description: '설문 상세 댓글 영역 위' },
  { value: 'ATTENDANCE', label: '출석 달력 하단', description: '출석체크 달력과 출석 버튼 사이' },
  { value: 'MAIN_FEED_IMAGE', label: '메인 피드 이미지', description: '메인 피드 이미지 강조 카드' },
  { value: 'ATTENDANCE_RESULT', label: '출석 완료', description: '출석 결과 페이지 이미지배너' },
  { value: 'EXCHANGE_RESULT', label: '환전 완료', description: '환전 결과 페이지 이미지배너' },
  { value: 'WEEKLY_MISSION_RESULT', label: '미션 완료', description: '이번주 미션 결과 페이지 이미지배너' },
];

interface AdFormModalProps {
  adId: number | null;
  onClose: () => void;
}

const AdFormModal = ({ adId, onClose }: AdFormModalProps) => {
  const isEdit = adId !== null;
  const queryClient = useQueryClient();

  /** States */
  const [adType, setAdType] = useState<AdType>('SURVEY');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [targetQuestionId, setTargetQuestionId] = useState<number | undefined>(undefined);
  const [selectedSurveyTitle, setSelectedSurveyTitle] = useState('');
  const [ctaText, setCtaText] = useState('참여하기');
  const [brandName, setBrandName] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [durationType, setDurationType] = useState<AdDurationType>('THREE_DAYS');
  const [sortOrder, setSortOrder] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [selectedPositions, setSelectedPositions] = useState<AdPosition[]>([]);
  const [positionWeights, setPositionWeights] = useState<Record<AdPosition, number>>({} as Record<AdPosition, number>);
  const [startDate, setStartDate] = useState(() => {
    return new Date().toISOString().slice(0, 16);
  });
  const [endDate, setEndDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 16);
  });
  const [iconType, setIconType] = useState<string>('megaphone');
  const [customImagePreview, setCustomImagePreview] = useState<string | null>(null);
  const [brandLogoUrl, setBrandLogoUrl] = useState<string | undefined>(undefined);
  const [imageUrl, setImageUrl] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [highlightPos, setHighlightPos] = useState<AdPosition | null>(null);
  const [showSurveyPicker, setShowSurveyPicker] = useState(false);
  const [surveySearchKeyword, setSurveySearchKeyword] = useState('');
  const [targetRoute, setTargetRoute] = useState('');
  const [targetRouteState, setTargetRouteState] = useState('');
  const [sharelinkPaste, setSharelinkPaste] = useState('');
  const [reviewRating, setReviewRating] = useState('');
  const [reviewCount, setReviewCount] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  /** 수정 모드: 기존 데이터 로드 */
  const { data: adDetail } = useQuery<AdminAdDetail>({
    queryKey: ['adminAdDetail', adId],
    queryFn: () => adApi.getAdDetail(adId!).then((res) => res.data),
    enabled: isEdit,
  });

  /** 활성 설문 목록 조회 (설문 선택 팝업용) */
  const { data: surveyList, isLoading: isSurveyLoading } = useQuery({
    queryKey: ['activeSurveys'],
    queryFn: () =>
      questionApi.getUnifiedQuestionList({
        page: 0,
        size: 100,
        filterType: 'all',
        sortByExpired: false,
      }),
    enabled: showSurveyPicker,
  });

  /** 설문 검색 필터 */
  const filteredSurveys = (surveyList?.content ?? []).filter(
    (q: QuestionV2) =>
      surveySearchKeyword === '' ||
      q.questionDescription.toLowerCase().includes(surveySearchKeyword.toLowerCase()) ||
      q.questionId.includes(surveySearchKeyword),
  );

  /** 설문 선택 핸들러 */
  const handleSelectSurvey = (survey: QuestionV2) => {
    setTargetQuestionId(Number(survey.questionId));
    setSelectedSurveyTitle(survey.questionDescription ?? '');
    setTitle(survey.questionDescription ?? '');
    setShowSurveyPicker(false);
    setSurveySearchKeyword('');
  };

  useEffect(() => {
    if (adDetail) {
      setAdType(adDetail.adType);
      setTitle(adDetail.title ?? '');
      setDescription(adDetail.description ?? '');
      setTargetQuestionId(adDetail.targetQuestionId ?? undefined);
      setCtaText(adDetail.ctaText ?? '참여하기');
      setBrandName(adDetail.brandName ?? '');
      setTargetUrl(adDetail.targetUrl ?? '');
      setDurationType(adDetail.durationType ?? 'CUSTOM');
      setSortOrder(adDetail.sortOrder);
      setIsActive(adDetail.isActive);
      setIconType(adDetail.iconType ?? 'megaphone');
      setTargetRoute(adDetail.targetRoute ?? '');
      setTargetRouteState(adDetail.targetRouteState ?? '');
      if (adDetail.iconType === 'custom' && adDetail.brandLogoUrl) {
        setCustomImagePreview(adDetail.brandLogoUrl);
        setBrandLogoUrl(adDetail.brandLogoUrl);
      }
      setImageUrl(adDetail.imageUrl ?? '');
      setReviewRating(adDetail.reviewRating != null ? String(adDetail.reviewRating) : '');
      setReviewCount(adDetail.reviewCount != null ? String(adDetail.reviewCount) : '');
      if (adDetail.positions?.length > 0) {
        setSelectedPositions(adDetail.positions.map((p) => p.position));
        setStartDate(adDetail.positions[0].startDate?.slice(0, 16) ?? '');
        setEndDate(adDetail.positions[0].endDate?.slice(0, 16) ?? '');
        const weights: Record<AdPosition, number> = {} as Record<AdPosition, number>;
        adDetail.positions.forEach((p) => {
          weights[p.position] = p.weight ?? 1;
        });
        setPositionWeights(weights);
      }
    }
  }, [adDetail]);

  /** 기간 프리셋 변경 */
  const handleDurationChange = (type: AdDurationType) => {
    setDurationType(type);
    if (type !== 'CUSTOM') {
      const days = DURATION_OPTIONS.find((d) => d.value === type)?.days ?? 0;
      const now = new Date();
      const effectiveStart = startDate ? new Date(startDate) : now;
      if (!startDate) {
        setStartDate(now.toISOString().slice(0, 16));
      }
      const end = new Date(effectiveStart);
      end.setDate(end.getDate() + days);
      setEndDate(end.toISOString().slice(0, 16));
    }
  };

  /** 시작일 변경 시 종료일 재계산 */
  const handleStartDateChange = (value: string) => {
    setStartDate(value);
    if (durationType !== 'CUSTOM') {
      const days = DURATION_OPTIONS.find((d) => d.value === durationType)?.days ?? 0;
      const start = new Date(value);
      start.setDate(start.getDate() + days);
      setEndDate(start.toISOString().slice(0, 16));
    }
  };

  /** 위치 토글 + 하이라이트 애니메이션 */
  const togglePosition = (pos: AdPosition) => {
    const willAdd = !selectedPositions.includes(pos);
    setSelectedPositions((prev) => (prev.includes(pos) ? prev.filter((p) => p !== pos) : [...prev, pos]));
    if (willAdd) {
      setHighlightPos(pos);
      setTimeout(() => setHighlightPos(null), 1500);
    }
  };

  /** 저장 */
  const saveMutation = useMutation({
    mutationFn: (params: CreateAdRequest) => {
      return isEdit ? adApi.updateAd(adId!, params) : adApi.createAd(params);
    },
    onSuccess: () => {
      toast.success(isEdit ? '광고가 수정되었습니다.' : '광고가 등록되었습니다.');
      queryClient.invalidateQueries({ queryKey: ['adminAds'] });
      onClose();
    },
    onError: () => toast.error('저장에 실패했습니다.'),
  });

  /** 쉐어링크 유형 선택 — 신규는 활성 꺼짐으로 시작 (대표가 확인 후 직접 켠다) */
  const selectSharelinkType = () => {
    setAdType('SHARELINK');
    if (!isEdit) setIsActive(false);
    if (ctaText === '참여하기') setCtaText('토스쇼핑에서 보기');
  };

  /** 쉐어링크 「채우기」 — 3줄 나누기 + 서버가 링크를 1번 열어 이미지·평점·리뷰 수 조회 */
  const handleSharelinkFill = async () => {
    const parsed = parseSharelinkPaste(sharelinkPaste);
    if (!parsed) {
      toast.error('쉐어링크 주소(https://toss.shopping/_m/…)가 있는 글을 붙여넣어 주세요.');
      return;
    }
    setTargetUrl(parsed.targetUrl);
    if (parsed.title) setTitle(parsed.title);
    if (parsed.description) setDescription(parsed.description);

    setIsResolving(true);
    try {
      const { data } = await adApi.resolveSharelink(parsed.targetUrl);
      setImageUrl(data.imageUrl);
      setReviewRating(data.reviewRating != null ? String(data.reviewRating) : '');
      setReviewCount(data.reviewCount != null ? String(data.reviewCount) : '');
      toast.success('상품 정보를 채웠어요.');
    } catch {
      toast.error('상품 이미지를 못 가져왔어요. 이미지 주소를 직접 넣어 주세요.');
    } finally {
      setIsResolving(false);
    }
  };

  /** 쉐어링크 필수값 검사 — 통과하면 null, 아니면 안내 문구 */
  const getSharelinkError = (): string | null => {
    if (!description.trim()) return '대가성 문구를 입력해주세요.';
    if (!isValidSharelinkUrl(targetUrl)) return '쉐어링크 주소는 https://toss.shopping/_m/… 형식이어야 해요.';
    if (!imageUrl.trim() || imageUrl.includes('/_m/')) return '상품 이미지 주소를 넣어 주세요.';
    return null;
  };

  const handleSubmit = () => {
    if (adType === 'SHARELINK') {
      const sharelinkError = getSharelinkError();
      if (sharelinkError) {
        toast.error(sharelinkError);
        return;
      }
    }
    if (!(title ?? '').trim()) {
      toast.error('광고 제목을 입력해주세요.');
      return;
    }
    if (selectedPositions.length === 0) {
      toast.error('노출 위치를 선택해주세요.');
      return;
    }
    if (!startDate || !endDate) {
      toast.error('기간을 설정해주세요.');
      return;
    }

    const positions: PositionItemRequest[] = selectedPositions.map((pos) => ({
      position: pos,
      startDate,
      endDate,
      weight: positionWeights[pos] ?? 1,
    }));

    const params: CreateAdRequest = {
      adType,
      title,
      description: description || undefined,
      targetQuestionId: adType === 'SURVEY' ? targetQuestionId : undefined,
      ctaText,
      iconType,
      brandLogoUrl: iconType === 'custom' ? brandLogoUrl : undefined,
      brandName: adType === 'EXTERNAL' ? brandName : undefined,
      targetUrl: adType === 'EXTERNAL' || adType === 'SHARELINK' ? targetUrl.trim() : undefined,
      targetRoute: adType === 'APP_ROUTE' ? targetRoute : undefined,
      targetRouteState: adType === 'APP_ROUTE' && targetRouteState ? targetRouteState : undefined,
      imageUrl: imageUrl || undefined,
      reviewRating: adType === 'SHARELINK' && reviewRating ? Number(reviewRating) : undefined,
      reviewCount: adType === 'SHARELINK' && reviewCount ? Number(reviewCount) : undefined,
      durationType,
      sortOrder,
      isActive,
      positions,
    };

    saveMutation.mutate(params);
  };

  /** 미리보기용 설문카드 스켈레톤 (실제 QuestionCardV2 축소 재현) */
  const PreviewQuestionCard = ({ num }: { num: number }) => (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      {/* 카테고리 뱃지 + 남은시간 */}
      <div className="flex items-center gap-1.5 mb-2">
        <span className="px-1.5 py-0.5 text-[9px] bg-gray-100 text-gray-600 rounded">🎯 일상</span>
        <span className="px-1.5 py-0.5 text-[9px] border border-green-200 text-green-600 bg-green-50 rounded">
          ⏰ {num}일 남음
        </span>
      </div>
      {/* 작성자 */}
      <div className="flex items-center gap-2 mb-1.5">
        <div className="w-5 h-5 rounded-full bg-gray-200" />
        <div>
          <div className="w-12 h-2 bg-gray-200 rounded" />
          <div className="w-8 h-1.5 bg-gray-100 rounded mt-0.5" />
        </div>
      </div>
      {/* 제목 */}
      <div className="w-full h-2.5 bg-gray-200 rounded mb-1" />
      <div className="w-3/4 h-2.5 bg-gray-100 rounded mb-2.5" />
      {/* 참여 버튼 */}
      <div className="h-6 bg-gray-900 rounded flex items-center justify-center">
        <span className="text-[9px] text-white font-medium">설문 참여하기</span>
      </div>
      <div className="text-right mt-1">
        <span className="text-[8px] text-gray-400">{num * 12}명 참여</span>
      </div>
    </div>
  );

  /** 미리보기용 광고 카드 (실제 AdCard 재현: border-dashed + blue 배경) */
  const PreviewAdCard = () => {
    const preset = getAdIconPreset(iconType);
    const IconComp = preset.icon;
    return (
      <div className="bg-blue-50/30 border border-dashed border-blue-200 rounded-lg p-3">
        <div className="flex items-center gap-2.5">
          {iconType === 'custom' && customImagePreview ? (
            <img src={customImagePreview} alt="" className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
          ) : (
            <div
              className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: preset.bgColor }}
            >
              <IconComp size={14} color={preset.iconColor} />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 mb-0.5">
              <span className="px-1 py-0 text-[8px] font-medium border border-blue-300 text-blue-600 rounded">
                광고
              </span>
            </div>
            <p className="text-[11px] font-medium text-gray-800 truncate">{title || '광고 제목이 여기에 표시됩니다'}</p>
            {description && <p className="text-[10px] text-gray-500 truncate">{description}</p>}
          </div>
          <ArrowRight className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
        </div>
      </div>
    );
  };

  /** 미리보기용 미니 배너 (실제 MiniBanner 재현: gradient + 아이콘) */
  const PreviewMiniBanner = () => {
    const preset = getAdIconPreset(iconType);
    const IconComp = preset.icon;
    return (
      <div className="flex items-center justify-between px-3 py-2.5 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-100">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {iconType === 'custom' && customImagePreview ? (
            <img src={customImagePreview} alt="" className="w-5 h-5 rounded-full object-cover flex-shrink-0" />
          ) : (
            <div
              className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: preset.bgColor }}
            >
              <IconComp size={12} color={preset.iconColor} />
            </div>
          )}
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-gray-700 truncate">{title || '광고 제목이 여기에 표시됩니다'}</p>
            {description && <p className="text-[9px] text-gray-500 truncate">{description}</p>}
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
          <ArrowRight className="w-3 h-3 text-gray-400" />
        </div>
      </div>
    );
  };

  /** 피드 위치 미리보기 */
  const FeedPreview = ({ position, label }: { position: AdPosition; label: string }) => {
    const isHighlighted = highlightPos === position;
    const isSelected = selectedPositions.includes(position);
    return (
      <div className={`transition-all duration-500 ${isSelected ? 'opacity-100' : 'opacity-30'}`}>
        <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">{label}</p>
        <div className="space-y-2">
          {/* 설문카드 4개 (축소) */}
          {[3, 7, 2, 5].map((num, i) => (
            <div key={i} className="transform scale-[0.85] origin-top-left" style={{ width: '117.6%' }}>
              <PreviewQuestionCard num={num} />
            </div>
          ))}
          {/* 광고 삽입 위치 */}
          <div
            className={`transition-all duration-500 ${isHighlighted ? 'ring-2 ring-blue-400 ring-offset-2 scale-[1.03]' : ''} rounded-lg`}
          >
            <div className="relative">
              {isHighlighted && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full whitespace-nowrap animate-bounce z-10">
                  여기에 노출!
                </div>
              )}
              <PreviewAdCard />
            </div>
          </div>
          {/* 이후 설문카드 */}
          <div className="transform scale-[0.85] origin-top-left" style={{ width: '117.6%' }}>
            <PreviewQuestionCard num={1} />
          </div>
        </div>
      </div>
    );
  };

  /** 출석 탭 미리보기 */
  const AttendancePreview = () => {
    const isHighlighted = highlightPos === 'ATTENDANCE';
    const isSelected = selectedPositions.includes('ATTENDANCE');
    return (
      <div className={`transition-all duration-500 ${isSelected ? 'opacity-100' : 'opacity-30'}`}>
        <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">출석 달력 하단</p>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* 연속 출석 뱃지 */}
          <div className="px-3 pt-3 pb-1">
            <div className="w-24 h-3 bg-blue-100 rounded" />
          </div>
          {/* 달력 */}
          <div className="px-3 py-2">
            <div className="w-16 h-2 bg-gray-100 rounded mb-2" />
            {/* 요일 헤더 */}
            <div className="grid grid-cols-7 gap-0.5 mb-1">
              {['일', '월', '화', '수', '목', '금', '토'].map((d) => (
                <div key={d} className="text-[7px] text-center text-gray-400">
                  {d}
                </div>
              ))}
            </div>
            {/* 날짜 그리드 */}
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: 35 }).map((_, i) => (
                <div
                  key={i}
                  className={`w-full aspect-square rounded-full flex items-center justify-center text-[7px] ${i < 5 ? 'bg-blue-400 text-white' : 'bg-gray-50 text-gray-400'}`}
                >
                  {i < 5 ? '✓' : i - 4 <= 26 ? i - 4 : ''}
                </div>
              ))}
            </div>
          </div>
          {/* 광고 삽입 위치 */}
          <div
            className={`mx-2 mb-2 transition-all duration-500 ${isHighlighted ? 'ring-2 ring-blue-400 ring-offset-1 scale-[1.02]' : ''} rounded-lg`}
          >
            <div className="relative">
              {isHighlighted && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full whitespace-nowrap animate-bounce z-10">
                  여기에 노출!
                </div>
              )}
              <PreviewAdCard />
            </div>
          </div>
          {/* 출석 버튼 */}
          <div className="px-3 pb-3">
            <div className="w-full h-7 bg-blue-500 rounded-lg flex items-center justify-center">
              <span className="text-[9px] text-white font-medium">오늘 출석하기</span>
            </div>
          </div>
        </div>
      </div>
    );
  };

  /** 상세 위치 미리보기 */
  const DetailPreview = () => {
    const isHighlighted = highlightPos === 'DETAIL_COMMENT';
    const isSelected = selectedPositions.includes('DETAIL_COMMENT');
    return (
      <div className={`transition-all duration-500 ${isSelected ? 'opacity-100' : 'opacity-30'}`}>
        <p className="text-[10px] font-semibold text-gray-500 mb-2 uppercase tracking-wide">상세 댓글 위</p>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {/* 설문 상세 헤더 영역 */}
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="px-1.5 py-0.5 text-[9px] bg-gray-100 text-gray-600 rounded">🎯 일상</span>
              <span className="px-1.5 py-0.5 text-[9px] border border-green-200 text-green-600 bg-green-50 rounded">
                ⏰ 3일 남음
              </span>
            </div>
            <div className="w-full h-2.5 bg-gray-200 rounded mb-1" />
            <div className="w-2/3 h-2.5 bg-gray-100 rounded" />
          </div>
          {/* 투표 영역 */}
          <div className="p-3 border-b border-gray-100 space-y-1.5">
            <div className="h-7 bg-gray-100 rounded-md flex items-center px-2">
              <div className="w-2/3 h-2 bg-blue-200 rounded" />
            </div>
            <div className="h-7 bg-gray-100 rounded-md flex items-center px-2">
              <div className="w-1/3 h-2 bg-gray-200 rounded" />
            </div>
          </div>
          {/* 미니 배너 삽입 위치 */}
          <div
            className={`mx-2 my-2 transition-all duration-500 ${isHighlighted ? 'ring-2 ring-blue-400 ring-offset-1 scale-[1.02]' : ''} rounded-lg`}
          >
            <div className="relative">
              {isHighlighted && (
                <div className="absolute -top-5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-blue-500 text-white text-[9px] font-bold rounded-full whitespace-nowrap animate-bounce z-10">
                  여기에 노출!
                </div>
              )}
              <PreviewMiniBanner />
            </div>
          </div>
          {/* 댓글 영역 */}
          <div className="p-3 space-y-2">
            <p className="text-[10px] font-medium text-gray-600">댓글 3개</p>
            {[1, 2].map((i) => (
              <div key={i} className="flex items-start gap-2">
                <div className="w-5 h-5 rounded-full bg-gray-200 flex-shrink-0" />
                <div className="flex-1">
                  <div className="w-10 h-1.5 bg-gray-200 rounded mb-1" />
                  <div className="w-full h-2 bg-gray-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white sm:rounded-xl shadow-xl w-full max-w-4xl h-full sm:h-auto sm:max-h-[90vh] flex overflow-hidden">
        {/* 왼쪽: 폼 */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* 헤더 */}
          <div className="flex items-center justify-between p-4 sm:p-6 border-b flex-shrink-0">
            <h3 className="text-base sm:text-lg font-bold">{isEdit ? '광고 수정' : '광고 등록'}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* 폼 */}
          <div className="p-4 sm:p-6 space-y-4 overflow-y-auto flex-1">
            {/* 타입 선택 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">광고 타입</label>
              <div className="flex gap-2">
                <Button
                  variant={adType === 'SURVEY' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAdType('SURVEY')}
                >
                  설문광고
                </Button>
                <Button
                  variant={adType === 'EXTERNAL' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAdType('EXTERNAL')}
                >
                  외부광고
                </Button>
                <Button
                  variant={adType === 'APP_ROUTE' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setAdType('APP_ROUTE')}
                >
                  앱내링크
                </Button>
                <Button
                  variant={adType === 'SHARELINK' ? 'default' : 'outline'}
                  size="sm"
                  onClick={selectSharelinkType}
                >
                  쉐어링크
                </Button>
              </div>
            </div>

            {/* 설문 선택 (설문광고) */}
            {adType === 'SURVEY' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">대상 설문</label>
                <div className="flex items-center gap-2">
                  <div
                    onClick={() => setShowSurveyPicker(true)}
                    className="flex-1 border rounded-md px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 transition-colors flex items-center justify-between min-h-[40px] gap-2"
                  >
                    {targetQuestionId ? (
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-gray-400 text-xs flex-shrink-0">#{targetQuestionId}</span>
                        <span className="text-gray-900 truncate">{selectedSurveyTitle}</span>
                      </div>
                    ) : (
                      <span className="text-gray-400">설문을 선택하세요</span>
                    )}
                    <Search className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  </div>
                  {targetQuestionId && (
                    <button
                      onClick={() => {
                        setTargetQuestionId(undefined);
                        setSelectedSurveyTitle('');
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded text-gray-400"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* 외부광고 URL (EXTERNAL) */}
            {adType === 'EXTERNAL' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">URL (딥링크)</label>
                <input
                  type="text"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                  placeholder="https://... 또는 intoss://앱이름"
                  className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            )}

            {/* 쉐어링크 (SHARELINK) — 토스에서 복사한 3줄 붙여넣기 */}
            {adType === 'SHARELINK' && (
              <div className="space-y-3 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">토스에서 복사한 글 붙여넣기</label>
                  <textarea
                    value={sharelinkPaste}
                    onChange={(e) => setSharelinkPaste(e.target.value)}
                    rows={3}
                    placeholder={'✱ 이 포스팅은 토스쇼핑 쉐어링크 활동의 일환으로 …\n상품명\nhttps://toss.shopping/_m/…'}
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button size="sm" className="mt-2" disabled={isResolving} onClick={handleSharelinkFill}>
                    {isResolving ? '가져오는 중…' : '채우기'}
                  </Button>
                  <p className="mt-1 text-xs text-gray-500">
                    상품명 · 대가성 문구 · 주소를 나눠 넣고, 상품 사진과 평점 · 리뷰 수를 가져와요. 금액은 넣지 않아요.
                  </p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">쉐어링크 주소</label>
                  <input
                    type="text"
                    value={targetUrl}
                    onChange={(e) => setTargetUrl(e.target.value)}
                    placeholder="https://toss.shopping/_m/…"
                    className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">평점 (0~5, 선택)</label>
                    <input
                      type="number"
                      step="0.1"
                      min={0}
                      max={5}
                      value={reviewRating}
                      onChange={(e) => setReviewRating(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1">
                    <label className="text-sm font-medium text-gray-700 mb-1 block">리뷰 수 (선택)</label>
                    <input
                      type="number"
                      min={0}
                      value={reviewCount}
                      onChange={(e) => setReviewCount(e.target.value)}
                      className="w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
                <p className="text-xs text-gray-500">
                  대가성 문구는 아래 「설명」 칸에 들어가요. 작은 배너 위치에만 연결해 주세요.
                </p>
              </div>
            )}

            {/* 앱내링크 프리셋 (APP_ROUTE) */}
            {adType === 'APP_ROUTE' && (
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">이동 경로</label>
                <div className="flex flex-wrap gap-2">
                  {APP_ROUTE_PRESETS.map((preset) => {
                    const isSelected =
                      targetRoute === preset.route && (targetRouteState || '') === (preset.state || '');
                    return (
                      <button
                        key={`${preset.route}-${preset.state ?? ''}`}
                        onClick={() => {
                          setTargetRoute(preset.route);
                          setTargetRouteState(preset.state ?? '');
                        }}
                        className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                          isSelected
                            ? 'bg-blue-50 border-blue-300 text-blue-700'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                        }`}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-gray-500">경로</label>
                    <Input value={targetRoute} onChange={(e) => setTargetRoute(e.target.value)} placeholder="/notice" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500">상태 (JSON)</label>
                    <Input
                      value={targetRouteState}
                      onChange={(e) => setTargetRouteState(e.target.value)}
                      placeholder='{"tab":1}'
                    />
                  </div>
                </div>
              </div>
            )}

            {/* 제목 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">제목</label>
              <Input value={title ?? ''} onChange={(e) => setTitle(e.target.value)} placeholder="광고 제목" />
            </div>

            {/* 설명 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">설명</label>
              <textarea
                className="w-full border rounded-md px-3 py-2 text-sm resize-none"
                rows={2}
                value={description ?? ''}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="광고 설명 (선택)"
              />
            </div>

            {/* CTA 텍스트 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">CTA 텍스트</label>
              <Input value={ctaText ?? ''} onChange={(e) => setCtaText(e.target.value)} placeholder="참여하기" />
            </div>

            {/* 아이콘 선택 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">아이콘</label>
              <div className="grid grid-cols-5 gap-2">
                {AD_ICON_PRESETS.map((preset) => {
                  const IconComp = preset.icon;
                  const isSelected = iconType === preset.id;
                  return (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        setIconType(preset.id);
                        setCustomImagePreview(null);
                        setBrandLogoUrl(undefined);
                      }}
                      className={`flex flex-col items-center gap-1 p-2 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-transparent hover:border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                          width: 40,
                          height: 40,
                          backgroundColor: preset.bgColor,
                        }}
                      >
                        <IconComp size={20} color={preset.iconColor} />
                      </div>
                      <span className="text-[10px] text-gray-500">{preset.label}</span>
                    </button>
                  );
                })}
              </div>
              {/* 직접 업로드 */}
              <div className="mt-3 flex items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const reader = new FileReader();
                    reader.onload = () => {
                      const img = new Image();
                      img.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = 80;
                        canvas.height = 80;
                        const ctx = canvas.getContext('2d')!;
                        const minSide = Math.min(img.width, img.height);
                        const sx = (img.width - minSide) / 2;
                        const sy = (img.height - minSide) / 2;
                        ctx.drawImage(img, sx, sy, minSide, minSide, 0, 0, 80, 80);
                        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
                        setIconType('custom');
                        setCustomImagePreview(dataUrl);
                        setBrandLogoUrl(dataUrl);
                      };
                      img.src = reader.result as string;
                    };
                    reader.readAsDataURL(file);
                    e.target.value = '';
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  className="gap-1.5"
                >
                  <Upload className="w-3.5 h-3.5" />
                  직접 업로드
                </Button>
                {customImagePreview && (
                  <div className="flex items-center gap-2">
                    <img
                      src={customImagePreview}
                      alt="커스텀 아이콘"
                      className="w-10 h-10 rounded-full object-cover border border-gray-200"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setIconType('megaphone');
                        setCustomImagePreview(null);
                        setBrandLogoUrl(undefined);
                      }}
                      className="p-1 hover:bg-gray-100 rounded text-gray-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* S6-24: 광고 이미지 URL (MAIN_FEED 이미지 카드용) */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                광고 이미지 <span className="text-gray-400 font-normal">(선택, MAIN_FEED 이미지 카드용)</span>
              </label>
              <input
                type="text"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="이미지 URL 입력 (https://...)"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {imageUrl && (
                <div className="mt-2 relative">
                  <img
                    src={imageUrl}
                    alt="미리보기"
                    className="w-full rounded-lg object-cover"
                    style={{ maxHeight: 200 }}
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <button
                    onClick={() => setImageUrl('')}
                    className="absolute top-1 right-1 p-1 bg-white rounded-full shadow hover:bg-gray-100"
                  >
                    <Trash2 className="w-3 h-3 text-gray-400" />
                  </button>
                </div>
              )}
              <div className="mt-1 flex flex-wrap gap-1">
                {[
                  { label: '투표 참여', url: 'https://static.toss.im/illusts-content/img-vote-result.png' },
                  { label: '설문 조사', url: 'https://static.toss.im/illusts-content/img-survey.png' },
                  { label: '출석 체크', url: 'https://static.toss.im/illusts-content/img-calendar-check.png' },
                ].map((preset) => (
                  <button
                    key={preset.label}
                    onClick={() => setImageUrl(preset.url)}
                    className="px-2 py-1 text-xs border border-gray-200 rounded hover:bg-gray-50"
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 위치 다중 선택 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">노출 위치</label>
              <div className="flex flex-wrap gap-2">
                {POSITION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => togglePosition(opt.value)}
                    className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                      selectedPositions.includes(opt.value)
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              {/* 선택된 위치별 가중치 입력 */}
              {selectedPositions.length > 0 && (
                <div className="mt-2 space-y-1.5">
                  {selectedPositions.map((pos) => {
                    const opt = POSITION_OPTIONS.find((o) => o.value === pos);
                    return (
                      <div key={pos} className="flex items-center gap-2 text-sm">
                        <span className="text-gray-600 w-24 truncate">{opt?.label}</span>
                        <label className="text-xs text-gray-400">가중치</label>
                        <input
                          type="number"
                          min={1}
                          value={positionWeights[pos] ?? 1}
                          onChange={(e) => {
                            const v = parseInt(e.target.value, 10);
                            if (!isNaN(v) && v >= 1) {
                              setPositionWeights((prev) => ({ ...prev, [pos]: v }));
                            }
                          }}
                          className="w-16 border rounded px-2 py-0.5 text-sm text-center"
                        />
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* 기간 프리셋 */}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">기간</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {DURATION_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleDurationChange(opt.value)}
                    className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                      durationType === opt.value
                        ? 'bg-blue-50 border-blue-300 text-blue-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500">시작일</label>
                  <Input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-xs text-gray-500">종료일</label>
                  <Input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    disabled={durationType !== 'CUSTOM'}
                  />
                </div>
              </div>
            </div>

            {/* 우선순위 + 활성 */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">우선순위</label>
                <Input type="number" value={String(sortOrder)} onChange={(e) => setSortOrder(Number(e.target.value))} />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">상태</label>
                <div className="flex items-center gap-2 h-10">
                  <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                  <span className="text-sm">{isActive ? '활성' : '비활성'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* 푸터 */}
          <div className="flex justify-end gap-2 p-4 sm:p-6 border-t flex-shrink-0">
            <Button variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button onClick={handleSubmit} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? '저장 중...' : isEdit ? '수정' : '등록'}
            </Button>
          </div>
        </div>

        {/* 오른쪽: 미리보기 패널 (모바일에서 숨김) */}
        <div className="hidden md:block w-[340px] bg-gray-50 border-l flex-shrink-0 overflow-y-auto">
          <div className="p-5">
            <h4 className="text-sm font-bold text-gray-700 mb-4">노출 위치 미리보기</h4>

            {selectedPositions.length === 0 ? (
              <div className="flex items-center justify-center h-48 text-sm text-gray-400">
                왼쪽에서 노출 위치를 선택하면
                <br />
                미리보기가 표시됩니다.
              </div>
            ) : (
              <div className="space-y-5">
                {selectedPositions.includes('MAIN_FEED_TOP') && (
                  <FeedPreview position="MAIN_FEED_TOP" label="메인 피드 상단" />
                )}
                {selectedPositions.includes('MAIN_FEED') && <FeedPreview position="MAIN_FEED" label="메인 피드" />}
                {selectedPositions.includes('POPULAR_FEED_TOP') && (
                  <FeedPreview position="POPULAR_FEED_TOP" label="인기설문 상단" />
                )}
                {selectedPositions.includes('POPULAR_FEED') && (
                  <FeedPreview position="POPULAR_FEED" label="인기설문 피드" />
                )}
                {selectedPositions.includes('MY_FEED_TOP') && (
                  <FeedPreview position="MY_FEED_TOP" label="내설문 상단" />
                )}
                {selectedPositions.includes('MY_FEED') && <FeedPreview position="MY_FEED" label="내설문 피드" />}
                {selectedPositions.includes('DETAIL_COMMENT') && <DetailPreview />}
                {selectedPositions.includes('ATTENDANCE') && <AttendancePreview />}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 설문 선택 팝업 */}
      {showSurveyPicker && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden">
            {/* 헤더 */}
            <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
              <h4 className="text-base font-bold text-gray-900">설문 선택</h4>
              <button
                onClick={() => {
                  setShowSurveyPicker(false);
                  setSurveySearchKeyword('');
                }}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* 검색 */}
            <div className="px-4 pt-3 pb-2 flex-shrink-0">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="제목 또는 ID로 검색"
                  value={surveySearchKeyword}
                  onChange={(e) => setSurveySearchKeyword(e.target.value)}
                  className="pl-9"
                />
              </div>
            </div>

            {/* 목록 */}
            <div className="flex-1 overflow-y-auto px-2 pb-2">
              {isSurveyLoading ? (
                <div className="py-8 text-center text-gray-500 text-sm">로딩 중...</div>
              ) : filteredSurveys.length === 0 ? (
                <div className="py-8 text-center text-gray-500 text-sm">
                  {surveySearchKeyword ? '검색 결과가 없습니다.' : '활성 설문이 없습니다.'}
                </div>
              ) : (
                filteredSurveys.map((survey: QuestionV2) => {
                  const isSelected = targetQuestionId === Number(survey.questionId);
                  const remaining = Math.max(
                    0,
                    Math.ceil((new Date(survey.endDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)),
                  );
                  return (
                    <button
                      key={survey.questionId}
                      onClick={() => handleSelectSurvey(survey)}
                      className={`w-full text-left px-3 py-3 rounded-lg mb-1 transition-colors ${
                        isSelected ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold text-gray-900 mb-1">{survey.questionDescription}</p>
                          {survey.questionDescription && (
                            <p className="text-xs text-gray-500 truncate mb-1">{survey.questionDescription}</p>
                          )}
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[11px] text-gray-400">#{survey.questionId}</span>
                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded ${
                                survey.isExpired
                                  ? 'bg-gray-100 text-gray-500'
                                  : 'bg-green-50 text-green-600 border border-green-200'
                              }`}
                            >
                              {survey.isExpired ? '종료' : `${remaining}일 남음`}
                            </span>
                            <span className="text-[11px] text-gray-400">{survey.username}</span>
                            <span className="text-[11px] text-gray-400">참여 {survey.totalVoteCount}명</span>
                          </div>
                        </div>
                        {isSelected && <Check className="w-5 h-5 text-blue-500 flex-shrink-0 mt-1" />}
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdFormModal;
